// The AI Assistant's Markdown, read into blocks - the subset ChatMarkdown.jsx
// describes, and its own ```stats, ```causes and ```chart blocks - for the
// chat window to draw (ChatMarkdown) and to export (chatExport.js). Each block
// is { type: "heading" | "paragraph" | "list" | "table" | "quote" |
// "code" | "rule" | "stats" | "causes" | "chart", ... }; inline Markdown is
// left in the text.

const FENCE = /^(\s*)(`{3,}|~{3,})\s*([\w#+.-]*)\s*$/;
const STATS = /^(?:stats|kpis?|figures)$/i;
const CAUSES = /^(?:causes|root[-_]?causes?|rca)$/i;
const CHART = /^(?:chart|graph|plot)$/i;
const CHART_TYPES = ["bar", "line", "area", "pie"];
const MAX_CHART_POINTS = 60;
const FOLLOW_UPS = /^(?:follow[-_]?ups?|suggestions)$/i;
const TONES = { alert: "alert", warn: "warn", warning: "warn", good: "good" };
const MAX_STATS = 6;
const MAX_CAUSES = 6;
// A cause's strength - the bars of its meter, and how it is said.
export const STRENGTHS = new Map([
  ["strong", { bars: 3, label: "Strong" }],
  ["moderate", { bars: 2, label: "Moderate" }],
  ["weak", { bars: 1, label: "Weak" }],
  ["unlikely", { bars: 0, label: "Unlikely" }],
  ["ruled out", { bars: 0, label: "Unlikely" }],
]);
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/;
const RULE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const TABLE_RULE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

const indentOf = (line) => line.match(/^\s*/)[0].length;
const isBlank = (line) => !line.trim();
const isOrdered = (marker) => /\d/.test(marker);

function splitRow(line) {
  let row = line.trim();
  if (row.startsWith("|")) row = row.slice(1);
  if (row.endsWith("|") && !row.endsWith("\\|")) row = row.slice(0, -1);
  return row.split(/(?<!\\)\|/).map((cell) => cell.trim().replace(/\\\|/g, "|"));
}

// A table: a row of cells over a row of dashes with as many cells.
function isTableStart(lines, i) {
  if (!lines[i].includes("|") || i + 1 >= lines.length) return false;
  const rule = lines[i + 1];
  return TABLE_RULE.test(rule) && rule.includes("-") && splitRow(rule).length === splitRow(lines[i]).length;
}

function startsBlock(lines, i) {
  const line = lines[i];
  return FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || ITEM.test(line) || isTableStart(lines, i);
}

// A fence closes on a line of its own character, at least as long.
function closesFence(line, fence) {
  const text = line.trim();
  return text.length >= fence.length && [...text].every((char) => char === fence[0]);
}

// A ```stats block's figures: "value | label" or "value | label | tone" a
// line (a list marker or bold around them is let go).
function parseStats(lines) {
  const items = [];
  for (const line of lines) {
    if (isBlank(line)) continue;
    const cells = splitRow(line.replace(/^\s*(?:[-*+]|\d{1,2}[.)])\s+/, "")).map((cell) => cell.replace(/\*\*|__/g, "").trim());
    if (cells.length < 2 || !cells[0] || !cells[1]) continue;
    items.push({ value: cells[0], label: cells[1], tone: TONES[(cells[2] || "").toLowerCase()] || null });
  }
  return items.slice(0, MAX_STATS);
}

// A ```causes block's causes: "strength | cause | evidence" a line - the
// strength found in whichever cell holds it, the cause and evidence the
// other cells in order (a list marker or bold around them is let go).
function parseCauses(lines) {
  const items = [];
  for (const line of lines) {
    if (isBlank(line)) continue;
    const cells = splitRow(line.replace(/^\s*(?:[-*+]|\d{1,2}[.)])\s+/, "")).map((cell) => cell.replace(/\*\*|__/g, "").trim());
    const at = cells.findIndex((cell) => STRENGTHS.has(cell.toLowerCase()));
    const rest = cells.filter((cell, column) => column !== at && cell);
    if (at === -1 || !rest.length) continue;
    items.push({ strength: cells[at].toLowerCase() === "ruled out" ? "unlikely" : cells[at].toLowerCase(), cause: rest[0], evidence: rest.slice(1).join(" | ") });
  }
  return items.slice(0, MAX_CAUSES);
}

// A figure as written in a chart: "1,234", "$54", "92%", "1,234 gal".
function chartNumber(cell) {
  const text = String(cell).replace(/[$,%\s]|gal(?:lons)?\b|meters?\b/gi, "");
  return /^-?\d+(?:\.\d+)?$/.test(text) ? Number(text) : null;
}

// A ```chart block as a chart (ChatVisuals' spec): its "type:", "title:",
// "unit:" and "series:" lines, and "label | value | value..." a point - a
// header row or a line of text passed over. Two points at the least.
function parseChart(lines) {
  const meta = {};
  const points = [];
  for (const line of lines) {
    if (isBlank(line)) continue;
    const setting = line.match(/^\s*(type|title|unit|series)\s*:\s*(.+?)\s*$/i);
    if (setting) {
      meta[setting[1].toLowerCase()] = setting[2];
      continue;
    }
    const cells = splitRow(line.replace(/^\s*(?:[-*+]|\d{1,2}[.)])\s+/, "")).map((cell) => cell.replace(/\*\*|__/g, "").trim());
    const values = cells.slice(1).map(chartNumber);
    if (cells.length < 2 || !cells[0] || values.some((value) => value === null)) continue;
    points.push({ x: cells[0], values });
  }
  if (points.length < 2) return null;
  const width = Math.max(...points.map((point) => point.values.length));
  const names = (meta.series || "").split(",").map((name) => name.trim()).filter(Boolean);
  // One series is "value", as category charts and pies read it.
  const keys = width === 1 ? ["value"] : Array.from({ length: width }, (_, n) => `s${n}`);
  const series = keys.map((key, n) => ({ key, label: names[n] || (width === 1 ? meta.title || "Value" : `Series ${n + 1}`) }));
  const type = CHART_TYPES.includes(String(meta.type).toLowerCase()) ? String(meta.type).toLowerCase() : "bar";
  return {
    id: "md",
    kind: "chart",
    type: width > 1 && type === "pie" ? "bar" : type,
    title: meta.title || "",
    unit: meta.unit,
    x_kind: "category",
    series,
    data: points.slice(0, MAX_CHART_POINTS).map((point) => ({ x: point.x, ...Object.fromEntries(keys.map((key, n) => [key, point.values[n] ?? null])) })),
    switch: width === 1 ? ["bar", "line", "pie"] : ["bar", "line"],
  };
}

// A chart block's chart, the same one while its text is the same: the text
// after it streaming in re-reads the answer, and a new one would be drawn
// (and animated) again each time.
const charts = new Map();
function chartOf(lines) {
  const text = lines.join("\n");
  if (!charts.has(text)) {
    const visual = parseChart(lines);
    charts.set(text, visual ? [visual] : null);
    if (charts.size > 50) charts.delete(charts.keys().next().value);
  }
  return charts.get(text);
}

function parseList(lines, start) {
  const first = lines[start].match(ITEM);
  const indent = first[1].length;
  const ordered = isOrdered(first[2]);
  const items = [];
  let i = start;
  while (i < lines.length) {
    // Blank lines between items: the list goes on if another item follows.
    if (isBlank(lines[i])) {
      let next = i;
      while (next < lines.length && isBlank(lines[next])) next += 1;
      const sibling = next < lines.length && lines[next].match(ITEM);
      if (!sibling || sibling[1].length !== indent || isOrdered(sibling[2]) !== ordered) break;
      i = next;
    }
    const item = lines[i].match(ITEM);
    if (!item || item[1].length !== indent || isOrdered(item[2]) !== ordered) break;
    // The item: its first line, then what is indented under it. Under a
    // numbered item, bullets straight after it at the same indent are taken
    // as its own (the model often forgets to indent them).
    const body = [item[3]];
    let adopted = false;
    i += 1;
    while (i < lines.length) {
      const line = lines[i];
      if (isBlank(line)) {
        let next = i;
        while (next < lines.length && isBlank(lines[next])) next += 1;
        if (next < lines.length && indentOf(lines[next]) > indent) {
          body.push(...lines.slice(i, next).map(() => ""));
          i = next;
          continue;
        }
        break;
      }
      const bullet = line.match(ITEM);
      if (ordered && bullet && bullet[1].length === indent && !isOrdered(bullet[2])) {
        adopted = true;
        body.push(`   ${line}`);
        i += 1;
        continue;
      }
      if (indentOf(line) > indent) {
        body.push(adopted ? `   ${line}` : line);
        i += 1;
        continue;
      }
      break;
    }
    const rest = body.slice(1);
    const depths = rest.filter((line) => !isBlank(line)).map(indentOf);
    const common = depths.length ? Math.min(...depths) : 0;
    items.push(parseBlocks([body[0], ...rest.map((line) => line.slice(Math.min(common, indentOf(line))))]));
  }
  return { block: { type: "list", ordered, start: ordered ? parseInt(first[2], 10) : 1, items }, next: i };
}

export function parseBlocks(lines) {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isBlank(line)) {
      i += 1;
      continue;
    }
    let match = line.match(FENCE);
    if (match) {
      const [, indent, fence, lang] = match;
      const body = [];
      i += 1;
      while (i < lines.length && !closesFence(lines[i], fence)) {
        body.push(lines[i].slice(Math.min(indent.length, indentOf(lines[i]))));
        i += 1;
      }
      i += 1;
      // Suggested questions: shown apart, under the answer.
      if (FOLLOW_UPS.test(lang)) continue;
      if (CAUSES.test(lang)) {
        // Causes with none in the expected form stay a code block, not lost.
        const causes = parseCauses(body);
        blocks.push(causes.length ? { type: "causes", items: causes } : { type: "code", lang, text: body.join("\n") });
        continue;
      }
      if (CHART.test(lang)) {
        // So does a chart with fewer than two points.
        const visuals = chartOf(body);
        blocks.push(visuals ? { type: "chart", visuals } : { type: "code", lang, text: body.join("\n") });
        continue;
      }
      const stats = STATS.test(lang) ? parseStats(body) : [];
      // Figures with none in the expected form stay a code block, not lost.
      blocks.push(stats.length ? { type: "stats", items: stats } : { type: "code", lang, text: body.join("\n") });
      continue;
    }
    match = line.match(HEADING);
    if (match) {
      blocks.push({ type: "heading", level: match[1].length, text: match[2] });
      i += 1;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push({ type: "rule" });
      i += 1;
      continue;
    }
    if (QUOTE.test(line)) {
      const body = [];
      while (i < lines.length && QUOTE.test(lines[i])) {
        body.push(lines[i].match(QUOTE)[1]);
        i += 1;
      }
      blocks.push({ type: "quote", children: parseBlocks(body) });
      continue;
    }
    if (isTableStart(lines, i)) {
      const header = splitRow(line);
      const align = splitRow(lines[i + 1]).map((cell) => {
        if (cell.startsWith(":") && cell.endsWith(":")) return "center";
        if (cell.endsWith(":")) return "right";
        if (cell.startsWith(":")) return "left";
        return undefined;
      });
      i += 2;
      const rows = [];
      while (i < lines.length && !isBlank(lines[i]) && lines[i].includes("|")) {
        const cells = splitRow(lines[i]);
        rows.push(header.map((_, column) => cells[column] ?? ""));
        i += 1;
      }
      blocks.push({ type: "table", header, align, rows });
      continue;
    }
    if (ITEM.test(line)) {
      const { block, next } = parseList(lines, i);
      blocks.push(block);
      i = next;
      continue;
    }
    const paragraph = [];
    while (i < lines.length && !isBlank(lines[i]) && !(paragraph.length && startsBlock(lines, i))) {
      paragraph.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: "paragraph", lines: paragraph });
  }
  return blocks;
}
